/**
 * `/dynamics/pinpoint` — what to fix in F&O, in what order, and what it costs.
 *
 * The board `/dynamics` says *what is slow*. This one says *what is worth a
 * ticket*: it names the specific (form, company) cells that clear the backend's
 * floors, ranks them by the person-hours of waiting they cause, says which layer
 * the evidence points at and which team the ticket goes to, and hands over the
 * activity ids someone opens a Microsoft support case with.
 *
 * ## Altitude
 *
 * **L1.** A KPI strip at the top (L0-in-page: waiting caused, findings,
 * companies affected, browser-telemetry coverage), then the ranked bar and the
 * findings list, then the per-company table. **L2** is the evidence drawer at
 * `?finding=<id>` — the full verdict reason, the AOS nodes, the exception
 * signatures, and the capture pack. Every drill lives in the URL, so an
 * investigation is a link.
 *
 * ## The ranking is the argument
 *
 * `excess_hours` is the ranking key and the order comes from the API; nothing on
 * this page re-sorts by ratio. That is the whole point: a 3.5× problem on a form
 * five hundred people open outranks an 18× one eight people hit. Because a
 * reader scanning the ratio column would otherwise be surprised by the order,
 * the rule is written on the section and the rank is printed on every card.
 *
 * ## Honesty
 *
 * - **Coverage is the load-bearing caveat.** These findings are built from
 *   F&O's server-side timing, which covers the whole estate; browser telemetry
 *   covered 1 company of 31 and 17 form loads of 269,725 when the board was
 *   written. The strip says both ratios and the basis sentence says what they
 *   mean, so no verdict here reads as fleet-measured. `coverage: null` renders
 *   as "not measured", never as a zero.
 * - **An empty findings list is a good outcome**, rendered as one — never as an
 *   error and never as an empty table.
 * - **`case_variants` is surfaced, not smoothed.** A company arriving in two
 *   case spellings was silently two companies until 2026-08-21; the table says
 *   so per row.
 * - **`access_state` decides whether anything renders at all** — the board's
 *   own `FoAccessPanel`, never an empty chart (§5).
 *
 * ## The gate
 *
 * The route is open to anyone who can reach `/dynamics`, and the payload answers
 * 403 unless the sign-in is on `NOVA_FO_INVESTIGATORS` — the same shape as
 * `/dynamics/investigate` and `/dynamics/reports`, which is why the sidebar
 * disables its row rather than hiding it. It names no people; it is gated
 * because a ranked list of which company is worst is an operational judgement
 * about named parts of the business. There is **no reason gate**: the route
 * carries no `reason`, writes no audit row, and so may poll like the other
 * aggregates.
 */

import { Icon, Target } from "@norican/icons";
import {
  Alert,
  type BreadcrumbItem,
  Breadcrumbs,
  Chip,
  Freshness,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { type FoConsoleLookback, useFoConsolePinpoint, useFoConsolePlatform } from "@/lib/api";
import { drillTo, useDrawerParam, useDrillParams } from "@/lib/drill";

import { FoAccessPanel, FoErrorsAlert, isPanelOk } from "./access-panel";
import { apiErrorDetail, apiErrorStatus, pinpointEvidenceBasis } from "./console-format";
import { LookbackRow, useLookbackParam } from "./lookback-row";
import {
  COMPANY_PARAM,
  DYNAMICS_PATH,
  DYNAMICS_PINPOINT_PATH,
  FINDING_PARAM,
  LOOKBACK_PARAM,
} from "./params";
import { PinpointCompanies } from "./pinpoint-companies";
import { PinpointDrawer } from "./pinpoint-drawer";
import { PinpointFindings } from "./pinpoint-findings";
import { PinpointKpiStrip } from "./pinpoint-kpis";
import { PlatformSection } from "./platform-section";
import { ConsoleToasts } from "./toast-stack";

/**
 * The window a bare `/dynamics/pinpoint` opens on.
 *
 * A week rather than the console's usual 24 h: `excess_hours` is a cumulative
 * cost, and the route this page reads is served for seven days by default. A
 * link that arrives from the board with `?lookback=` still wins, so the two
 * screens agree whenever one drilled into the other.
 */
const PINPOINT_LOOKBACK: FoConsoleLookback = "7d";

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Dynamics",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to={DYNAMICS_PATH} className={className}>
        {children}
      </Link>
    ),
  },
  { label: "Pinpoint", current: true },
];

export function DynamicsPinpointPage() {
  return (
    <ConsoleToasts>
      <PinpointPageBody />
    </ConsoleToasts>
  );
}

function PinpointPageBody() {
  const lookback = useLookbackParam(PINPOINT_LOOKBACK);
  const drill = useDrillParams(COMPANY_PARAM);
  const drawer = useDrawerParam(FINDING_PARAM);

  const pinpoint = useFoConsolePinpoint(lookback);
  const platform = useFoConsolePlatform(lookback);
  const view = pinpoint.data;
  const status = apiErrorStatus(pinpoint.error);
  const staleAfterMs = view ? view.cache_ttl_sec * 2000 : 0;

  const companyFilter = drill.params[COMPANY_PARAM] ?? null;
  const allFindings = view?.findings ?? [];
  const findings = companyFilter
    ? allFindings.filter((finding) => finding.company === companyFilter)
    : allFindings;
  const openFinding = allFindings.find((finding) => finding.id === drawer.value) ?? null;
  const filteredCompany = view?.companies.find((row) => row.company === companyFilter);

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="What to fix first in F&O"
        subtitle="The forms and companies worth a ticket, ranked by the person-hours of waiting they cause — with the evidence a support case is opened with."
        actions={
          view?.collected_at ? (
            <Freshness
              label="Workspace read"
              since={view.collected_at}
              staleAfter={staleAfterMs}
              pulse={pinpoint.isFetching}
            />
          ) : undefined
        }
      />

      <LookbackRow scopes="this whole board" fallback={PINPOINT_LOOKBACK} />

      {pinpoint.error && status === 403 && (
        <Alert tone="warning" title="This sign-in is not on the investigator list">
          {apiErrorDetail(pinpoint.error) ??
            "The ranking names no people, but it does rank named parts of the business, so it opens only for the investigators named in NOVA's configuration. The Dynamics board's aggregates stay available."}
        </Alert>
      )}
      {pinpoint.error && status !== 403 && (
        <Alert tone="danger" title="Couldn't load the pinpoint board">
          {status === 0
            ? "The NOVA API did not respond. This is a NOVA problem, not an Azure one."
            : (apiErrorDetail(pinpoint.error) ??
              `The NOVA API answered ${status ?? "with an error"}.`)}
        </Alert>
      )}

      {pinpoint.isPending && !view && !pinpoint.error && (
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height="8rem" />
            ))}
          </div>
          <Skeleton height="22rem" />
          <Skeleton height="18rem" />
        </div>
      )}

      {view && !isPanelOk(view) && (
        <FoAccessPanel panel="What to fix first in F&O (pinpoint)" state={view} />
      )}

      {view && isPanelOk(view) && (
        <>
          {view.errors.length > 0 && <FoErrorsAlert errors={view.errors} />}

          <PinpointKpiStrip pinpoint={view} findings={findings} />

          {/* The basis sentence sits above everything it qualifies, because it
              qualifies every verdict below it. */}
          <Alert
            tone="neutral"
            icon={<Icon icon={Target} size="sm" />}
            title="What these findings are measured with"
          >
            {pinpointEvidenceBasis(view.coverage)}
          </Alert>

          {companyFilter && (
            <div className="flex flex-wrap items-center gap-2">
              <Typography variant="caption" tone="muted">
                Findings filtered to:
              </Typography>
              <Chip
                size="sm"
                onRemove={() => drill.set({ [COMPANY_PARAM]: null })}
                removeLabel="Show every company again"
              >
                {filteredCompany?.company_name ?? companyFilter} ({companyFilter})
              </Chip>
            </div>
          )}

          {drawer.value != null && openFinding == null && (
            // A shared link whose finding has aged out of the window. Say that,
            // rather than opening an empty drawer or silently ignoring the URL.
            <Alert tone="neutral" title="That finding is not in this window">
              The link names <code className="break-all">{drawer.value}</code>, and the ranking for
              this lookback does not contain it. Findings are rebuilt per window, so one that
              cleared the floors last week may not this week. Widen the lookback, or clear the
              selection.
            </Alert>
          )}

          <PinpointFindings
            findings={findings}
            totalFindings={allFindings.length}
            companyFilter={companyFilter}
            lookback={lookback}
            collectedAt={view.collected_at}
            staleAfterMs={staleAfterMs}
            onOpen={(findingId) => drawer.open(findingId)}
          />

          <PinpointCompanies
            companies={view.companies}
            lookback={lookback}
            selected={companyFilter}
            onSelect={(company) => drill.set({ [COMPANY_PARAM]: company })}
          />

          {/* Below the findings on purpose. The batch jobs are not what any of
              the findings above are about — nobody's screen is waiting on them —
              and the synthetic tests are the control the findings are read
              against, so both belong after the thing they qualify. */}
          {/* The platform payload carries its own access state and its own
              errors, and they are not the pinpoint payload's. Rendering the
              section against `data` alone made "the query failed", "the
              workspace is unreadable", "not configured" and "still loading" all
              print the same sentence: "no batch job threw in this window".
              Which is good news, and three of the four are not. */}
          {platform.data == null ? (
            <Skeleton height="20rem" />
          ) : isPanelOk(platform.data) ? (
            <>
              {platform.data.errors.length > 0 && <FoErrorsAlert errors={platform.data.errors} />}
              <PlatformSection platform={platform.data} lookback={lookback} />
            </>
          ) : (
            <FoAccessPanel
              panel="Batch jobs and the platform's own front door"
              state={platform.data}
            />
          )}
        </>
      )}

      <PinpointDrawer
        finding={openFinding}
        lookback={lookback}
        onClose={drawer.close}
        breadcrumbs={[
          { label: "Dynamics", to: DYNAMICS_PATH },
          {
            label: "Pinpoint",
            to: drillTo(DYNAMICS_PINPOINT_PATH, {
              [LOOKBACK_PARAM]: lookback,
              [COMPANY_PARAM]: companyFilter,
            }),
          },
        ]}
      />
    </main>
  );
}
